
import React, { useState } from 'react';
import MainLayout from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { UserPlus, ChevronDown } from 'lucide-react';
import { useIsMobile, useIsTablet } from '@/hooks/use-mobile';
import UsersList from '@/components/users/UsersList';
import UserForm from '@/components/users/UserForm';
import UserDeleteDialog from '@/components/users/UserDeleteDialog';
import UserPasswordResetDialog from '@/components/users/UserPasswordResetDialog';
import UserFilters from '@/components/users/UserFilters';
import { useUsers, UserWithMembership } from '@/hooks/useUsers';
import { useUserForm } from '@/hooks/useUserForm';
import { useUserOperations } from '@/hooks/useUserOperations';
import { useUserFilters } from '@/hooks/users/useUserFilters';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const UserManagement = () => {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isFullScreen = isMobile || isTablet;
  const { t } = useTranslation();

  const { users, loading, profile, organization } = useUsers();
  const { currentUserRole } = useAuth();
  const {
    searchTerm,
    setSearchTerm,
    selectedRole,
    setSelectedRole,
    showActiveOnly,
    setShowActiveOnly,
    showInactiveOnly,
    setShowInactiveOnly,
    filteredUsers,
  } = useUserFilters(users);
  const { formData, setFormData, resetForm, setFormDataForEdit } =
    useUserForm();
  const {
    isDeleting,
    operationLoading,
    isResettingPassword,
    isTogglingStatus,
    handleCreateUser,
    handleUpdateUser,
    handleDeleteUser,
    handleResetPassword,
    handleToggleMembershipStatus,
  } = useUserOperations({
    organization,
  });

  const [isCreateUserOpen, setIsCreateUserOpen] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserWithMembership | null>(
    null
  );
  const [userToDelete, setUserToDelete] = useState<UserWithMembership | null>(
    null
  );
  const [userToResetPassword, setUserToResetPassword] =
    useState<UserWithMembership | null>(null);

  if (!profile || currentUserRole !== 'admin') {
    return null;
  }

  const isAdmin = currentUserRole === 'admin';

  const handleEditUser = (user: UserWithMembership) => {
    setEditingUser(user);
    setFormDataForEdit(user);
  };

  const handleCloseCreateDialog = () => {
    setIsCreateUserOpen(false);
    resetForm();
  };

  const handleOpenCreateDialog = () => {
    resetForm();
    setIsCreateUserOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditingUser(null);
    resetForm();
  };

  const onCreateSubmit = async (e: React.FormEvent) => {
    await handleCreateUser(e, formData);
    if (!operationLoading) {
      handleCloseCreateDialog();
    }
  };

  const onUpdateSubmit = async (e: React.FormEvent) => {
    if (editingUser) {
      await handleUpdateUser(e, editingUser, formData);
      if (!operationLoading) {
        handleCloseEditDialog();
      }
    }
  };

  const onDeleteConfirm = async () => {
    if (userToDelete) {
      await handleDeleteUser(userToDelete);
      if (!isDeleting) {
        setUserToDelete(null);
      }
    }
  };

  const onResetPasswordConfirm = async (newPassword: string) => {
    if (userToResetPassword) {
      return await handleResetPassword(userToResetPassword, newPassword);
    }
    return false;
  };

  const CreateUserTrigger = (
    <Button className="flex items-center gap-2">
      <UserPlus className="h-5 w-5" />
      {!isMobile && t('users.addUser')}
    </Button>
  );

  const CreateUserContent = (
    <>
      <UserForm
        formData={formData}
        setFormData={setFormData}
        onSubmit={onCreateSubmit}
        loading={operationLoading}
        isEdit={false}
      />
    </>
  );

  const EditUserContent = (
    <>
      <UserForm
        formData={formData}
        setFormData={setFormData}
        onSubmit={onUpdateSubmit}
        loading={operationLoading}
        isEdit={true}
        editingUser={editingUser}
      />
    </>
  );

  return (
    <MainLayout>
      <div className="py-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-4">{t('users.title')}</h1>
          
          {isFullScreen ? (
            <Sheet open={isCreateUserOpen} onOpenChange={(open) => {
              if (open) {
                handleOpenCreateDialog();
              } else {
                handleCloseCreateDialog();
              }
            }}>
              <SheetTrigger asChild>
                <Button size="lg" className="w-full sm:w-auto">
                  <UserPlus className="h-5 w-5" />
                  {t('users.addUser')}
                </Button>
              </SheetTrigger>
              <SheetContent
                side="bottom"
                className={`${isTablet ? 'h-screen w-full rounded-none border-0 p-0 flex flex-col modal-safe-area-aware ios-modal-safe pwa-modal-header-fix' : 'h-screen w-full rounded-none border-0 p-0 flex flex-col modal-safe-area-aware ios-modal-safe pwa-modal-header-fix'}`}
              >
                <SheetHeader className={`${isTablet ? 'p-6 border-b flex-shrink-0' : 'p-6 border-b flex-shrink-0'}`}>
                  <SheetTitle>{t('users.addUser')}</SheetTitle>
                </SheetHeader>
                <div className={`${isTablet ? 'flex-1 p-6 pb-safe-bottom overflow-y-auto' : 'flex-1 p-6 overflow-y-auto'}`}>
                  {CreateUserContent}
                </div>
              </SheetContent>
            </Sheet>
          ) : (
            <Dialog open={isCreateUserOpen} onOpenChange={(open) => {
              if (open) {
                handleOpenCreateDialog();
              } else {
                handleCloseCreateDialog();
              }
            }}>
              <DialogTrigger asChild>
                <Button size="lg" className="flex items-center gap-2">
                  <UserPlus className="h-5 w-5" />
                  {t('users.addUser')}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('users.addUser')}</DialogTitle>
                </DialogHeader>
                {CreateUserContent}
              </DialogContent>
            </Dialog>
          )}
        </div>

        {loading ? (
          <div className="text-center py-8">{t('common.loading')}...</div>
        ) : (
          <>
            <Collapsible open={isFiltersOpen} onOpenChange={setIsFiltersOpen} className="mb-6">
              <CollapsibleTrigger asChild>
                <Button 
                  variant="outline" 
                  className="flex items-center gap-2 mb-4"
                >
                  <ChevronDown className={`h-4 w-4 transition-transform ${isFiltersOpen ? 'rotate-180' : ''}`} />
                  {t('users.filters')}
                  {!isFiltersOpen && filteredUsers.length !== users.length && (
                    <span className="ml-2 text-xs bg-muted px-2 py-1 rounded">
                      {filteredUsers.length}/{users.length}
                    </span>
                  )}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <UserFilters
                  searchTerm={searchTerm}
                  onSearchChange={setSearchTerm}
                  selectedRole={selectedRole}
                  onRoleChange={setSelectedRole}
                  showActiveOnly={showActiveOnly}
                  onActiveOnlyChange={setShowActiveOnly}
                  showInactiveOnly={showInactiveOnly}
                  onInactiveOnlyChange={setShowInactiveOnly}
                />
              </CollapsibleContent>
            </Collapsible>
            <UsersList
              users={filteredUsers}
              onEdit={handleEditUser}
              onDelete={setUserToDelete}
              onResetPassword={setUserToResetPassword}
              onToggleStatus={handleToggleMembershipStatus}
              isAdmin={isAdmin}
              isTogglingStatus={isTogglingStatus}
            />
          </>
        )}

        {/* Edit User Dialog */}
        {isFullScreen ? (
          <Sheet open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
            <SheetContent
              side="bottom"
              className={`${isTablet ? 'h-screen w-full rounded-none border-0 p-0 flex flex-col modal-safe-area-aware ios-modal-safe pwa-modal-header-fix' : 'h-screen w-full rounded-none border-0 p-0 flex flex-col modal-safe-area-aware ios-modal-safe pwa-modal-header-fix'}`}
            >
              <SheetHeader className={`${isTablet ? 'p-6 border-b flex-shrink-0' : 'p-6 border-b flex-shrink-0'}`}>
                <SheetTitle>{t('users.editUser')}</SheetTitle>
              </SheetHeader>
              <div className={`${isTablet ? 'flex-1 p-6 pb-safe-bottom overflow-y-auto' : 'flex-1 p-6 overflow-y-auto'}`}>
                {EditUserContent}
              </div>
            </SheetContent>
          </Sheet>
        ) : (
          <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('users.editUser')}</DialogTitle>
              </DialogHeader>
              {EditUserContent}
            </DialogContent>
          </Dialog>
        )}

        {/* Delete User Dialog */}
        <UserDeleteDialog
          user={userToDelete}
          open={!!userToDelete}
          onOpenChange={(open) => !open && setUserToDelete(null)}
          onConfirm={onDeleteConfirm}
          isDeleting={isDeleting}
        />

        {/* Reset Password Dialog */}
        <UserPasswordResetDialog
          user={userToResetPassword}
          open={!!userToResetPassword}
          onOpenChange={(open) => !open && setUserToResetPassword(null)}
          onConfirm={onResetPasswordConfirm}
          isResetting={isResettingPassword}
        />
      </div>
    </MainLayout>
  );
};

export default UserManagement;
